import isNil from 'lodash/isNil';
import { useSymbolMessages } from 'widgets/symbolTile/store/data/hooks';
import {
    SymbolTilesSymbolBarItem,
    SymbolTilesSymbolState,
} from 'widgets/symbolTile/store/data/SymbolTilesStreamState';
import { StreamMessage } from 'utils/messages/StreamMessage';
import { NumberFormat } from '@instinet-analytics/core-lib';
import { sortTimeSeries } from 'utils/timeseries/timeSeriesUtils';
import { TimeSeriesChartDataItem } from 'utils/components/TimeSeriesChart/TimeSeriesChartData';
import {
    applyShowDataFor,
    ShowDataFor,
} from 'utils/components/ShowDataFor/ShowDataFor';
import { safeCalculatePercent } from 'utils/percent';
import { useCapsuleBoundSymbol } from 'widgets/tiles/components/tile/hooks';
import { applyTickSize, applyTickSizeWithComparison } from 'utils/tickSize';
import {
    MarketDataBarItem,
    MarketDataTickItem,
} from 'marketData/store/MarketDataState';
import cloneDeep from 'lodash/cloneDeep';

export interface SymbolTileDataTimeSeriesItem extends TimeSeriesChartDataItem {
    _i: number;
    price?: number;
    low?: number;
    high?: number;
    last?: number;
    formattedValues: {
        price: string;
        absChange: string;
        percChange: string;
        className: string;
    };
}
export interface SymbolTileData {
    formattedValues: {
        price: string;
        bid: string;
        ask: string;
        pnc: string;
        high: string;
        low: string;
        absChange: string;
        percChange: string;
        className: string;
    };
    startTime?: number;
    endTime?: number;
    timeSeries: SymbolTileDataTimeSeriesItem[];
}

export function useSymbolTileMessages(): StreamMessage[] {
    const { symbol } = useCapsuleBoundSymbol();
    return useSymbolMessages(symbol);
}

function getItemFormattedValues(
    numberFormat: NumberFormat,
    streamData: SymbolTilesSymbolState,
    b: SymbolTilesSymbolBarItem
) {
    const { tickSize } = streamData;
    const price = applyTickSize(numberFormat, tickSize, b.closePrice);

    const absChangeValue = b.closePrice - streamData.pnc;
    const sign = absChangeValue >= 0 ? '+' : '';
    const absChange = `${sign}${applyTickSize(
        numberFormat,
        tickSize,
        absChangeValue
    )}`;

    const percChangeValue = safeCalculatePercent(
        absChangeValue,
        streamData.pnc
    );
    const percChange = `${sign}${percChangeValue.toFixed(2)}%`;

    const className =
        absChangeValue > 0 ? 'up' : absChangeValue < 0 ? 'down' : 'equal';

    return { absChange, className, percChange, price };
}

export function buildSymbolTileData(
    numberFormat: NumberFormat,
    streamData: SymbolTilesSymbolState,
    externalMarketBarData: MarketDataBarItem[] | undefined,
    externalMarketDataLatestTick: MarketDataTickItem | undefined,
    showDataFor?: ShowDataFor
): SymbolTileData | undefined {
    if (streamData.currency === '') {
        return undefined;
    }

    if (externalMarketBarData && externalMarketDataLatestTick) {
        return buildSymbolTileDataFromExternalMarketData(
            numberFormat,
            streamData,
            externalMarketBarData,
            externalMarketDataLatestTick,
            showDataFor
        );
    }

    return buildSymbolTileDataFromStream(numberFormat, streamData, showDataFor);
}

export function buildSymbolTileDataFromStream(
    numberFormat: NumberFormat,
    streamData: SymbolTilesSymbolState,
    showDataFor?: ShowDataFor
): SymbolTileData | undefined {
    if (streamData.currency === '') {
        return undefined;
    }
    const { tickSize } = streamData;

    const filteredBars = applyShowDataFor(streamData.bars, showDataFor);

    const boundaries = filteredBars.reduce<{ high?: number; low?: number }>(
        (values, item) => {
            return {
                high: isNil(values.high)
                    ? item.high
                    : Math.max(values.high, item.high),
                low: isNil(values.low)
                    ? item.low
                    : Math.min(values.low, item.low),
            };
        },
        {}
    );

    const price = applyTickSize(
        numberFormat,
        tickSize,
        streamData.lastTradePrice
    );
    const pnc = applyTickSize(numberFormat, tickSize, streamData.pnc);
    const ask = applyTickSizeWithComparison(
        numberFormat,
        tickSize,
        streamData.ask,
        streamData.lastTradePrice
    );
    const bid = applyTickSizeWithComparison(
        numberFormat,
        tickSize,
        streamData.bid,
        streamData.lastTradePrice
    );

    const high = applyTickSize(numberFormat, tickSize, boundaries.high);
    const low = applyTickSize(numberFormat, tickSize, boundaries.low);

    let absChange = '';
    let percChange = '';
    if (!isNil(streamData.lastTradePriceChangePercent)) {
        const sign = streamData.lastTradePriceChangePercent >= 0 ? '+' : '';
        absChange = `${sign}${applyTickSize(
            numberFormat,
            tickSize,
            streamData.lastTradePriceChange
        )}`;
        percChange = `${sign}${streamData.lastTradePriceChangePercent.toFixed(
            2
        )}%`;
    }

    const toTimeSeriesItem: (
        b: SymbolTilesSymbolBarItem
    ) => SymbolTileDataTimeSeriesItem = (b) => ({
        _i: b._i,
        price: b.closePrice,
        time: b._i * 60000,
        formattedValues: getItemFormattedValues(numberFormat, streamData, b),
    });
    const timeSeries = sortTimeSeries(filteredBars.map(toTimeSeriesItem));

    let startTime: number | undefined = undefined;
    let endTime: number | undefined = undefined;

    if (timeSeries.length > 1) {
        startTime = timeSeries[0]._i * 60000;
        endTime = timeSeries[timeSeries.length - 1]._i * 60000;

        const itemWithLowestLow = filteredBars.sort((a, b) =>
            a.low > b.low ? +1 : -1
        )[0];
        const timeSeriesItemWithLowestLow = timeSeries.find(
            (t) => t._i === itemWithLowestLow._i
        );
        timeSeriesItemWithLowestLow!.low = itemWithLowestLow!.low;

        const itemWithHighestHigh = filteredBars.sort((a, b) =>
            a.high < b.high ? +1 : -1
        )[0];
        const timeSeriesItemWithHighestHigh = timeSeries.find(
            (t) => t._i === itemWithHighestHigh._i
        );
        timeSeriesItemWithHighestHigh!.high = itemWithHighestHigh!.high;

        const last = timeSeries[timeSeries.length - 1];
        last.last = last.price;
    }

    const className = !isNil(streamData.lastTradePriceChangePercent)
        ? streamData.lastTradePriceChangePercent! > 0
            ? 'up'
            : streamData.lastTradePriceChangePercent! < 0
            ? 'down'
            : 'equal'
        : '';

    return {
        formattedValues: {
            price,
            bid,
            ask,
            pnc,
            high,
            low,
            absChange,
            percChange,
            className,
        },
        timeSeries,
        startTime,
        endTime,
    };
}

function buildSymbolTileDataFromExternalMarketData(
    numberFormat: NumberFormat,
    streamData: SymbolTilesSymbolState,
    externalMarketBarData: MarketDataBarItem[],
    externalMarketDataLatestTick: MarketDataTickItem,
    showDataFor: ShowDataFor | undefined
): SymbolTileData | undefined {
    const enrichedStreamData = enrichStreamDataFromExternalMarketData(
        streamData,
        externalMarketBarData,
        externalMarketDataLatestTick
    );
    return buildSymbolTileDataFromStream(
        numberFormat,
        enrichedStreamData,
        showDataFor
    );
}

function enrichStreamDataFromExternalMarketData(
    streamData: SymbolTilesSymbolState,
    externalMarketBarData: MarketDataBarItem[],
    externalMarketDataLatestTick: MarketDataTickItem
): SymbolTilesSymbolState {
    const enriched = cloneDeep(streamData);
    const { pnc } = enriched;
    const { ask, bid, lastPrice } = externalMarketDataLatestTick;

    enriched.ask = ask ?? 0;
    enriched.bid = bid ?? 0;
    enriched.lastTradePrice = lastPrice ?? 0;
    enriched.lastTradePriceChange = enriched.lastTradePrice - pnc;
    enriched.lastTradePriceChangePercent = safeCalculatePercent(
        enriched.lastTradePriceChange,
        pnc
    );

    for (const externalBar of externalMarketBarData) {
        enriched.bars.push({
            _i: externalBar.time / 60000,
            low: externalBar.low ?? 0,
            high: externalBar.high ?? 0,
            closePrice: externalBar.close ?? 0,
            percentChange: safeCalculatePercent(
                (externalBar.close ?? 0) - pnc,
                pnc
            ),
        });
    }

    return enriched;
}
